/**
 * Secure redirect utilities to prevent open redirect vulnerabilities
 * Implements OWASP best practices for URL validation
 */

// Whitelist of allowed redirect paths
const ALLOWED_REDIRECT_PATHS = [
  '/',
  '/borrower',
  '/lender',
];

/**
 * Validate if a URL is safe for redirect
 * Prevents open redirect attacks by checking against whitelist
 * @param {string} url - URL to validate
 * @returns {boolean} - True if URL is safe to redirect to
 */
export function isSafeRedirect(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  // Empty or just whitespace
  if (!trimmedUrl) {
    return false;
  }

  // Check if URL is in whitelist (exact match)
  if (ALLOWED_REDIRECT_PATHS.includes(trimmedUrl)) {
    return true;
  }

  // Allow relative URLs that start with / but not //
  // This prevents protocol-relative URLs like //evil.com
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    return true;
  }

  // Block all absolute URLs (http://, https://, //example.com, etc.)
  // to prevent redirects to external sites
  if (
    trimmedUrl.startsWith('http://') ||
    trimmedUrl.startsWith('https://') ||
    trimmedUrl.startsWith('//') ||
    trimmedUrl.includes('://')
  ) {
    return false;
  }

  // Block javascript: and data: URLs
  const lowerUrl = trimmedUrl.toLowerCase();
  if (
    lowerUrl.startsWith('javascript:') ||
    lowerUrl.startsWith('data:') ||
    lowerUrl.startsWith('vbscript:') ||
    lowerUrl.startsWith('file:')
  ) {
    return false;
  }

  // If we get here, it's a relative path without a leading slash
  // This could be dangerous, so reject it
  return false;
}

/**
 * Safely redirect to a URL, falling back to home page if unsafe
 * @param {string} url - URL to redirect to
 * @param {string} fallback - Fallback URL if validation fails (default: '/')
 */
export function safeRedirect(url, fallback = '/') {
  if (isSafeRedirect(url)) {
    window.location.href = url;
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Blocked unsafe redirect attempt to:', url);
    }
    window.location.href = fallback;
  }
}

/**
 * Get a safe redirect URL from query parameters
 * @param {string} paramName - Query parameter name (default: 'redirect')
 * @returns {string|null} - Safe redirect URL or null if invalid
 */
export function getSafeRedirectFromQuery(paramName = 'redirect') {
  try {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get(paramName);

    if (redirectUrl && isSafeRedirect(redirectUrl)) {
      return redirectUrl;
    }

    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error parsing redirect query parameter:', error);
    }
    return null;
  }
}
